import React from "react";
import { Target, Eye, Handshake, Leaf, Buildings, UsersThree } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";

const VALUES = [
  { icon: Target, title: "Accountability", body: "Documented inspections, named site managers, and quarterly business reviews keep us honest." },
  { icon: Handshake, title: "Integrity", body: "We honor the contract — the small details that don't appear on a checklist still get done." },
  { icon: Leaf, title: "Sustainability", body: "Green Seal chemistries, microfiber programs, and energy-aware day-porter routes." },
  { icon: UsersThree, title: "People First", body: "Living-wage commitment, paid training, and a career ladder from porter to account manager." },
];

const TIMELINE = [
  { year: "2000", text: "Empire Serv founded in Asheboro, NC with two crews and three local accounts." },
  { year: "2007", text: "Expanded into medical-grade cleaning; first hospital contract awarded." },
  { year: "2013", text: "ISO 9001 systems implemented across all accounts; QA program standardized." },
  { year: "2018", text: "Registered in SAM.gov; first federal courthouse contract won." },
  { year: "2021", text: "GBAC STAR™ accreditation added — pandemic-grade disinfection capability." },
  { year: "2025", text: "Servicing 500+ facilities daily across the Carolinas with 250+ teammates." },
];

export default function About() {
  return (
    <>
      <SEO title="About Empire Serv" description="Empire Serv is a facilities maintenance company built around accountability, integrity, and sustainability. Learn our story, mission, and leadership." path="/about" />
      <PageHero
        eyebrow="About Empire Serv"
        title="A facilities partner built for the long contract."
        subtitle="Founded in Asheboro, NC and proudly self-performing — 25+ years bringing industrial-grade discipline to medical, government, and corporate facilities."
        image="https://images.pexels.com/photos/8273515/pexels-photo-8273515.jpeg"
        testId="about-hero"
      />

      <section className="es-section bg-white">
        <div className="es-container grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="es-eyebrow">Our Story</div>
            <h2 className="es-h2 text-[var(--es-navy)] mt-3">From a two-crew startup to a regional facilities partner.</h2>
            <div className="mt-6 space-y-4 text-[var(--es-text-muted)] leading-relaxed">
              <p>Empire Serv began on a simple promise: deliver the cleaning service you actually paid for, every shift. That promise grew us from local office accounts into hospitals, federal facilities, and 1M+ sq ft industrial plants.</p>
              <p>Today, our self-performing workforce, ISO-aligned playbooks, and QA documentation keep us auditable, contract-ready, and trusted by facility managers across the Carolinas.</p>
              <p>We're large enough to scale — and small enough that the owner's email is still on the contract.</p>
            </div>
          </div>
          <aside className="lg:col-span-5 bg-[var(--es-bg)] border border-[var(--es-border)] p-7 rounded-sm">
            <div className="flex items-center gap-3 mb-5">
              <Eye size={22} className="text-[var(--es-accent)]" weight="fill" />
              <h3 className="font-bold text-[var(--es-navy)] text-lg" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Mission</h3>
            </div>
            <p className="text-[var(--es-text-muted)] leading-relaxed">To set the benchmark for facilities maintenance — where cleanliness is measurable, our people are valued, and our clients can confidently defend the next option year.</p>
            <div className="mt-6 pt-6 border-t border-[var(--es-border)] flex items-center gap-3">
              <Buildings size={22} className="text-[var(--es-accent)]" weight="fill" />
              <div>
                <div className="font-bold text-[var(--es-navy)]">Headquarters</div>
                <div className="text-sm text-[var(--es-text-muted)]">15 S. Fayetteville Street, Suite 203-F, Asheboro, NC 27203</div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="es-section bg-[var(--es-bg)]">
        <div className="es-container">
          <div className="es-eyebrow">Our Values</div>
          <h2 className="es-h2 text-[var(--es-navy)] mt-3 max-w-3xl">Four principles, every site, every shift.</h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <div key={v.title} data-testid={`about-value-${i}`} className="es-card p-7">
                <v.icon size={26} weight="duotone" className="text-[var(--es-accent)]" />
                <h3 className="mt-4 text-lg font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{v.title}</h3>
                <p className="mt-2 text-sm text-[var(--es-text-muted)] leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="es-section bg-white">
        <div className="es-container grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="es-eyebrow">Milestones</div>
            <h2 className="es-h2 text-[var(--es-navy)] mt-3">25 years of compounded discipline.</h2>
          </div>
          <div className="lg:col-span-8">
            <ol className="relative border-l-2 border-[var(--es-border)] ml-2 space-y-8">
              {TIMELINE.map((t) => (
                <li key={t.year} className="pl-6 relative" data-testid={`timeline-${t.year}`}>
                  <span className="absolute -left-[9px] top-1 w-4 h-4 bg-[var(--es-accent)] rounded-sm" />
                  <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--es-accent)]">{t.year}</div>
                  <div className="mt-1 text-[var(--es-text)] leading-relaxed">{t.text}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <CTASection eyebrow="Working with us" title="Let's talk about your facility." subtitle="Tell us your scope and timeline — we'll match it with people, process, and price." />
    </>
  );
}
